// filepath: apps/web/src/features/observability/queries.ts
//
// FR-UI-DASH-1 -- TanStack Query hooks for the tenant observability
// dashboard surface.
//
// All hooks include `tenantSlug` in the cache key as the 2nd segment
// (INV-1, FR-UI-CORE-1 pattern; mirrors `procKeys.failedInstances`).
// Switching tenants does NOT leak the prior tenant's metrics or
// alerts -- the cache key changes, the query refetches.
//
// The 60-second refetch interval (AC-4) is applied at the
// TanStack Query level (`refetchInterval: 60_000`) so the cadence
// lives with the data fetch layer, not the page chrome.
//
// 5-error-state contract (FR-UI-CORE-10):
//   - 5xx / network     -> "fetch-failure"
//   - 403               -> "permission-denied"
//   - 404 / 410 (stale) -> "stale-version"
//   - 429 (Retry-After) -> "rate-limit"
//   - 200 + empty body  -> success (empty result)
//
// All errors are typed `ApiError` and surfaced via the page's
// classifyDashboardError helper (see ObservabilityDashboardPage.tsx).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";
import { observabilityKeys } from "./types";
import type {
  ActiveAlert,
  AlertRulePayload,
  AlertRulePersistError,
  ProcessDefinitionBreakdown,
  ProcessDefinitionBreakdownRow,
  TenantMetricsSummary,
} from "./types";

/** Use the canonical SPA API base path. Tests can override via
 *  MSW URL patterns (see apps/web/src/test/msw/handlers.ts). */
const DEFAULT_API_BASE = "/api";

/** Read Retry-After header (numeric seconds) for 429 backpressure. */
function parseRetryAfter(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (Number.isFinite(n) && n >= 0) return n;
  // Retry-After may be an HTTP-date; treat non-numeric as unknown.
  return null;
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

/**
 * useMetricsSummary -- GET /api/metrics/summary.
 *
 * Returns the typed `TenantMetricsSummary` envelope (mirrors
 * internal/obs/metrics_handler.go:TenantMetricsResponse). The
 * endpoint is tenant-scoped server-side via the kernel resolver
 * middleware; the SPA trusts the wire contract and renders exactly
 * what the server returns (INV-2 / FR-AUTH-7).
 *
 * AC-4: 60-second refetch interval for dashboard refresh cadence.
 * staleTime: 30_000 (30s) so a manual refresh within 30s triggers
 * a fresh fetch.
 */
export function useMetricsSummary(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  return useQuery<TenantMetricsSummary, ApiError>({
    queryKey: observabilityKeys.metricsSummary(tenantSlug),
    queryFn: async ({ signal }) => {
      let response: Response;
      try {
        response = await apiFetch(`${apiBase}/metrics/summary`, {
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const wire = (await response.json()) as TenantMetricsSummary;
      return normalise(wire);
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,
  });
}

/**
 * useProcessDefinitionBreakdown -- GET
 * /api/metrics/instances-by-process-definition (ISS-f4fa1b7b-268 /
 * FR-UI-DASH-1 AC-1 widget 4).
 *
 * Mirrors useMetricsSummary: tenantSlug as 2nd cache-key segment
 * (INV-1), 60s refetchInterval (AC-4), 30s staleTime, NOT
 * 404-tolerant -- the endpoint ships in the same MR as this hook,
 * so a 404 is a genuine error, not a "not yet shipped" envelope
 * (unlike useActiveAlerts).
 */
export function useProcessDefinitionBreakdown(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  return useQuery<ProcessDefinitionBreakdown, ApiError>({
    queryKey: observabilityKeys.processBreakdown(tenantSlug),
    queryFn: async ({ signal }) => {
      let response: Response;
      try {
        response = await apiFetch(
          `${apiBase}/metrics/instances-by-process-definition`,
          {
            init: {
              headers: { Accept: "application/json" },
              signal,
            },
          },
        );
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const wire = (await response.json()) as ProcessDefinitionBreakdown;
      return normaliseBreakdown(wire);
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,
  });
}

/**
 * useActiveAlerts -- GET /api/observability/alerts/active.
 *
 * Returns the typed `ActiveAlert[]` envelope. The endpoint does
 * NOT yet exist (FR-OBS-4 is not started); the SPA tolerates 404
 * by returning an empty array (mirrors the FR-UI-ADM-12
 * ISS-f4fa1b7b-250 typed-envelope contract). The panel renders
 * the typed empty state with `data-state="awaiting_fr_obs_4"`.
 *
 * Cache-key includes tenantSlug as the 2nd segment (INV-1).
 */
export function useActiveAlerts(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  return useQuery<ActiveAlert[], ApiError>({
    queryKey: observabilityKeys.activeAlerts(tenantSlug),
    queryFn: async ({ signal }) => {
      let response: Response;
      try {
        response = await apiFetch(`${apiBase}/observability/alerts/active`, {
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      // FR-OBS-4 not yet shipped -> endpoint returns 404 or 501.
      // The SPA treats both as "no active alerts" -- the panel
      // renders the typed empty state. When FR-OBS-4 ships, the
      // endpoint returns 200 with the array envelope.
      if (
        response.status === 404 ||
        response.status === 501 ||
        response.status === 503
      ) {
        return [] as ActiveAlert[];
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const wire = (await response.json()) as ActiveAlert[];
      return Array.isArray(wire) ? wire : [];
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,
  });
}

/**
 * useAlertRulePersist -- POST /api/observability/alerts/rules.
 *
 * Sends the typed `AlertRulePayload` body with an
 * `X-Idempotency-Key: crypto.randomUUID()` header (INV-3 / ARCH
 * Rule 3). Returns the persisted rule id on 200.
 *
 * On 404 or 501 (FR-OBS-4 not yet shipped) throws a typed
 * `AlertRulePersistError` so the AlertRuleAuthoringDialog can
 * render the InlineFieldError chrome with the
 * "alert-rule persistence will ship with FR-OBS-4" text and
 * preserve the validated source string verbatim (AC-2a).
 */
export function useAlertRulePersist(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    { rule_id: string },
    AlertRulePersistError,
    AlertRulePayload
  >({
    mutationFn: async (body) => {
      let response: Response;
      try {
        response = await apiFetch(`${apiBase}/observability/alerts/rules`, {
          method: "POST",
          init: {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
          idempotencyKey: true,
        });
      } catch (networkErr) {
        throw {
          code: "fr_obs_4_pending" as const,
          message:
            networkErr instanceof Error ? networkErr.message : "network error",
        } satisfies AlertRulePersistError;
      }

      if (
        response.status === 404 ||
        response.status === 501 ||
        response.status === 503
      ) {
        const env = await safeReadJson(response);
        throw {
          code: "fr_obs_4_pending" as const,
          message:
            (env as { message?: string } | null)?.message ??
            "alert-rule persistence will ship with FR-OBS-4",
          hint: (env as { hint?: string } | null)?.hint,
        } satisfies AlertRulePersistError;
      }

      if (!response.ok) {
        const env = await safeReadJson(response);
        throw {
          code: "fr_obs_4_pending" as const,
          message:
            (env as { message?: string } | null)?.message ??
            `http_${response.status}`,
        } satisfies AlertRulePersistError;
      }

      return (await response.json()) as { rule_id: string };
    },
    onSuccess: () => {
      // Invalidate the active-alerts panel so a freshly-saved
      // rule (if FR-OBS-4 is partially implemented server-side)
      // appears immediately. The refetch interval will catch up
      // regardless.
      qc.invalidateQueries({
        queryKey: observabilityKeys.activeAlerts(tenantSlug),
      });
    },
  });
}

/** Defensive normaliser for the breakdown envelope: `rows` is
 *  always an array (server contract sends `[]`, never null -- this
 *  guards against a malformed intermediary) and every row's numeric
 *  fields default to 0 so the bar renderer never sees undefined. */
function normaliseBreakdown(
  wire: ProcessDefinitionBreakdown,
): ProcessDefinitionBreakdown {
  const rows: ProcessDefinitionBreakdownRow[] = Array.isArray(wire?.rows)
    ? wire.rows
    : [];
  return {
    rows: rows.map((row) => ({
      process_definition_id: row?.process_definition_id ?? "",
      name: row?.name ?? "",
      instance_count: row?.instance_count ?? 0,
      failed_count: row?.failed_count ?? 0,
    })),
  };
}

/** Defensive normaliser for the metrics envelope. Guarantees the
 *  consumer receives a fully-shaped object (never `null` /
 *  `undefined`) so the renderer's per-widget access is safe. */
function normalise(wire: TenantMetricsSummary): TenantMetricsSummary {
  return {
    tenant_id: wire.tenant_id ?? "",
    slug: wire.slug ?? "",
    instance_count: wire.instance_count ?? 0,
    error_count: wire.error_count ?? 0,
    active_sandbox_count: wire.active_sandbox_count ?? 0,
    step_count: wire.step_count ?? 0,
    step_error_count: wire.step_error_count ?? 0,
    step_duration_buckets: Array.isArray(wire.step_duration_buckets)
      ? wire.step_duration_buckets
      : [],
    step_duration_p50_ms: wire.step_duration_p50_ms ?? 0,
    step_duration_p95_ms: wire.step_duration_p95_ms ?? 0,
    step_duration_p99_ms: wire.step_duration_p99_ms ?? 0,
    effect_http_success: wire.effect_http_success ?? 0,
    effect_http_retry: wire.effect_http_retry ?? 0,
    effect_http_dead_letter: wire.effect_http_dead_letter ?? 0,
    effect_http_latency_p50: wire.effect_http_latency_p50 ?? 0,
    effect_http_latency_p95: wire.effect_http_latency_p95 ?? 0,
    effect_http_latency_p99: wire.effect_http_latency_p99 ?? 0,
    effect_email_success: wire.effect_email_success ?? 0,
    effect_email_retry: wire.effect_email_retry ?? 0,
    effect_email_dead_letter: wire.effect_email_dead_letter ?? 0,
    effect_email_latency_p50: wire.effect_email_latency_p50 ?? 0,
    effect_email_latency_p95: wire.effect_email_latency_p95 ?? 0,
    effect_email_latency_p99: wire.effect_email_latency_p99 ?? 0,
    computed_at: wire.computed_at ?? "",
    error_rate_sparkline: Array.isArray(wire.error_rate_sparkline)
      ? wire.error_rate_sparkline
      : [],
  };
}
